import React, {
  useState,
  useMemo,
  useCallback,
  memo,
  useEffect,
  lazy,
  Suspense,
} from "react";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Stack,
  Chip,
  Grid,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  Close,
  Edit,
  Delete,
  CalendarToday,
  LocationOn,
  Category,
  Assessment,
  AttachMoney,
  Info,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { LoadingSpinner } from "../components";
import ItemCard from "../components/inventory/ItemCard";
import MasonryGrid from "../components/inventory/MasonryGrid";
import PhotoCarousel from "../components/common/PhotoCarousel";
import { ItemForm } from "../components";
import { useItems } from "../hooks/useItems";
import { api } from "../services/supabaseApi";
import { ItemFilters, Item } from "@catalog/types";
import { useSearch } from "../App";
import { useDebounce } from "../hooks/useDebounce";

// Lazy load components for better initial load performance
const VirtualizedMasonryGrid = lazy(
  () => import("../components/inventory/VirtualizedMasonryGrid")
);
// Performance: Lazy load the expanded item view to reduce initial bundle size
const ExpandedItemView = lazy(
  () => import("../components/inventory/ExpandedItemView")
);

// Memoized color utilities to prevent recalculation
const statusColors = {
  staged: "primary",
  in_storage: "success",
  unavailable: "error",
  default: "default",
} as const;

// Memoized helper functions with proper typing
const getStatusColor = (
  status: string
): "primary" | "success" | "error" | "default" => {
  return (
    statusColors[status as keyof typeof statusColors] || statusColors.default
  );
};

// Memoized responsive grid configuration to prevent recalculation
const useResponsiveGridConfig = (selectedItem: Item | null) => {
  const isLargeScreen = useMediaQuery("(min-width:1600px)");

  return useMemo(() => {
    if (!selectedItem) return { maxColumns: undefined, gridPadding: {} };

    const maxColumns = isLargeScreen ? 3 : 2;
    const gridPadding = {
      // Mobile: Bottom padding for sheet
      "@media (max-width:900px)": {
        pb: "140px",
      },
      // Small tablets: Compact padding
      "@media (min-width:900px) and (max-width:1200px)": {
        pl: "calc(46vw + 8px)",
      },
      // Medium screens: Optimized padding
      "@media (min-width:1200px) and (max-width:1600px)": {
        pl: "clamp(550px, calc(48vw + 16px), 850px)",
      },
      // Large screens: Maximum padding
      "@media (min-width:1600px)": {
        pl: "clamp(632px, calc(48vw + 16px), 932px)",
      },
    };

    return { maxColumns, gridPadding };
  }, [selectedItem, isLargeScreen]);
};

// Delete Confirmation Dialog - extracted and memoized
const DeleteConfirmDialog = memo<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemName: string;
}>(({ open, onClose, onConfirm, itemName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ pb: { xs: 1, md: 2 }, px: { xs: 2, md: 3 } }}>
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }}>
          <Delete color="error" sx={{ fontSize: { xs: 20, md: 24 } }} />
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: "1.125rem", md: "1.25rem" } }}
          >
            Confirm Delete
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, md: 3 }, pt: { xs: 1, md: 2 } }}>
        <Typography>
          Are you sure you want to delete "{itemName}"? This action cannot be
          undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: { xs: 1, md: 0 } }}>
        <Button
          onClick={onClose}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" },
            px: { xs: 2, md: 3 },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          startIcon={<Delete />}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" },
            px: { xs: 2, md: 3 },
          }}
        >
          Delete Item
        </Button>
      </DialogActions>
    </Dialog>
  );
});

// Virtualization threshold - use virtualized grid for large datasets
const VIRTUALIZATION_THRESHOLD = 100;

const InventoryPage: React.FC = () => {
  const theme = useTheme();
  const [filters, setFilters] = useState<ItemFilters>({});
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<Item | null>(null);
  const [itemToDelete, setItemToDelete] = useState<Item | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const { searchValue, activeFilters, removeFilter, addFilter } = useSearch();
  const queryClient = useQueryClient();

  // Debounce search to prevent excessive filtering
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const { data: items, isLoading, error: itemsError } = useItems();
  const {
    data: categories,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: api.categories.getAll,
  });
  const {
    data: locations,
    isLoading: locationsLoading,
    error: locationsError,
  } = useQuery({
    queryKey: ["locations"],
    queryFn: api.locations.getAll,
  });
  const {
    data: merchants,
    isLoading: merchantsLoading,
    error: merchantsError,
  } = useQuery({
    queryKey: ["merchants"],
    queryFn: api.merchants.getAll,
  });

  // Get responsive grid configuration (memoized)
  const { maxColumns, gridPadding } = useResponsiveGridConfig(selectedItem);

  // Comprehensive loading and error state handling
  const isAnyLoading =
    isLoading || categoriesLoading || locationsLoading || merchantsLoading;
  const hasErrors =
    itemsError || categoriesError || locationsError || merchantsError;

  // Sync local filters with global active filters
  useEffect(() => {
    const newFilters: ItemFilters = {};

    // Collect all merchant IDs for OR filtering
    const merchantIds = activeFilters
      .filter((filter) => filter.type === "merchant")
      .map((filter) => filter.id);

    // Collect all category IDs for OR filtering
    const categoryIds = activeFilters
      .filter((filter) => filter.type === "category")
      .map((filter) => filter.id);

    if (merchantIds.length > 0) {
      newFilters.merchant_ids = merchantIds; // Use array for multiple merchants
    }

    if (categoryIds.length > 0) {
      newFilters.category_ids = categoryIds; // Use array for multiple categories
    }

    setFilters(newFilters);

    // Show notification for multiple filters
    const notifications = [];
    const merchantNames = activeFilters
      .filter((filter) => filter.type === "merchant")
      .map((filter) => filter.name);
    const categoryNames = activeFilters
      .filter((filter) => filter.type === "category")
      .map((filter) => filter.name);

    if (merchantIds.length > 1) {
      notifications.push(
        `${merchantIds.length} merchants: ${merchantNames.join(", ")}`
      );
    }

    if (categoryIds.length > 1) {
      notifications.push(
        `${categoryIds.length} categories: ${categoryNames.join(", ")}`
      );
    }

    if (
      notifications.length > 0 ||
      (merchantIds.length > 0 && categoryIds.length > 0)
    ) {
      let message = "";

      if (merchantIds.length > 0 && categoryIds.length > 0) {
        // Both types active - explain AND logic
        const merchantText =
          merchantIds.length === 1
            ? `merchant "${merchantNames[0]}"`
            : `merchants: ${merchantNames.join(", ")}`;
        const categoryText =
          categoryIds.length === 1
            ? `category "${categoryNames[0]}"`
            : `categories: ${categoryNames.join(", ")}`;
        message = `Showing items from ${merchantText} AND in ${categoryText}`;
      } else if (notifications.length > 0) {
        // Only one type with multiple filters - explain OR logic
        message = `Showing items from ${notifications.join(", ")}`;
      }

      if (message) {
        setSnackbar({
          open: true,
          message,
          severity: "info",
        });
      }
    }
  }, [activeFilters]);

  // Optimized filtering with debounced search
  const filteredItems = useMemo(() => {
    if (!items) return [];

    return items.filter((item: Item) => {
      // Debounced text search - most selective filter first
      if (debouncedSearchValue) {
        const searchTerm = debouncedSearchValue.toLowerCase();
        const matchesSearch =
          item.name.toLowerCase().includes(searchTerm) ||
          item.description?.toLowerCase().includes(searchTerm);
        if (!matchesSearch) return false;
      }

      // Category filter with OR logic (early return for performance)
      if (filters.category_ids && filters.category_ids.length > 0) {
        if (
          !item.category_id ||
          !filters.category_ids.includes(item.category_id)
        ) {
          return false;
        }
      }

      // Merchant filter with OR logic (early return for performance)
      if (filters.merchant_ids && filters.merchant_ids.length > 0) {
        if (
          !item.merchant_id ||
          !filters.merchant_ids.includes(item.merchant_id)
        ) {
          return false;
        }
      }

      // Status filter
      if (filters.status && item.status !== filters.status) {
        return false;
      }

      // Price range filter with early exits
      const price = item.purchase_price;
      if (filters.min_price && price < filters.min_price) {
        return false;
      }
      if (filters.max_price && price > filters.max_price) {
        return false;
      }

      return true;
    });
  }, [items, filters, debouncedSearchValue]);

  // Performance: Throttled handlers to prevent excessive state updates
  const handleViewItem = useCallback((item: Item) => {
    setSelectedItem(item);

    // Smooth scroll to top when item is selected (especially from search)
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const handleCloseExpanded = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const handleEditItem = useCallback((item: Item) => {
    setItemToEdit(item);
    setIsEditFormOpen(true);
  }, []);

  const handleDeleteItem = useCallback((item: Item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleEditFormClose = () => {
    setIsEditFormOpen(false);
    setItemToEdit(null);
  };

  const handleEditFormSubmit = async (updatedItem: any) => {
    try {
      // Invalidate and refetch items
      await queryClient.invalidateQueries({ queryKey: ["items"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });

      setSnackbar({
        open: true,
        message: `Successfully updated "${updatedItem.name}"!`,
        severity: "success",
      });

      setIsEditFormOpen(false);
      setItemToEdit(null);

      // Update the selected item if it's the one being edited
      if (selectedItem?.id === updatedItem.id) {
        setSelectedItem(updatedItem);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update item. Please try again.",
        severity: "error",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await api.items.delete(itemToDelete.id);

      // Invalidate and refetch items
      await queryClient.invalidateQueries({ queryKey: ["items"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });

      setSnackbar({
        open: true,
        message: `Successfully deleted "${itemToDelete.name}"!`,
        severity: "success",
      });

      setIsDeleteDialogOpen(false);
      setItemToDelete(null);

      // Close expanded view if the deleted item was selected
      if (selectedItem?.id === itemToDelete.id) {
        setSelectedItem(null);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete item. Please try again.",
        severity: "error",
      });
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Enhanced items with category and location data (memoized)
  const enhancedItems = useMemo(() => {
    return filteredItems.map((item) => ({
      ...item,
      category: categories?.find((c) => c.id === item.category_id),
      location: locations?.find((l) => l.id === item.location_id),
    }));
  }, [filteredItems, categories, locations]);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle URL parameter for item selection and filtering
  useEffect(() => {
    // Don't process URL parameters if data is still loading to avoid race conditions
    if (isAnyLoading) return;

    const itemId = searchParams.get("item");
    const merchantId = searchParams.get("merchant");
    const categoryId = searchParams.get("category");

    if (itemId && items) {
      const targetItem = items.find((item: Item) => item.id === itemId);
      if (targetItem) {
        handleViewItem(targetItem);
        // Remove the item parameter from URL after selecting
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("item");
          return newParams;
        });
      }
    } else if (merchantId && merchants) {
      // Filter by merchant
      const merchant = merchants.find((m) => m.id === merchantId);
      if (merchant) {
        // Add filter to global context
        addFilter({
          id: merchantId,
          type: "merchant",
          name: merchant.name,
        });
        // Show notification about applied filter
        setSnackbar({
          open: true,
          message: `Filtered inventory by merchant: ${merchant.name}`,
          severity: "success",
        });
        // Remove the merchant parameter from URL after applying filter
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("merchant");
          return newParams;
        });
      }
    } else if (categoryId && categories) {
      // Filter by category
      const category = categories.find((c) => c.id === categoryId);
      if (category) {
        // Add filter to global context
        addFilter({
          id: categoryId,
          type: "category",
          name: category.name,
        });
        // Show notification about applied filter
        setSnackbar({
          open: true,
          message: `Filtered inventory by category: ${category.name}`,
          severity: "success",
        });
        // Remove the category parameter from URL after applying filter
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("category");
          return newParams;
        });
      }
    }
  }, [
    items,
    searchParams,
    setSearchParams,
    handleViewItem,
    merchants,
    categories,
    addFilter,
    isAnyLoading,
  ]);

  // Keyboard shortcuts for enhanced UX
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!selectedItem) return;

      // Edit shortcut: Ctrl/Cmd + E
      if ((event.ctrlKey || event.metaKey) && event.key === "e") {
        event.preventDefault();
        handleEditItem(selectedItem);
      }

      // Delete shortcut: Delete key (only when not in an input)
      if (
        event.key === "Delete" &&
        !["INPUT", "TEXTAREA"].includes((event.target as HTMLElement).tagName)
      ) {
        event.preventDefault();
        handleDeleteItem(selectedItem);
      }

      // Close expanded view: Escape key
      if (event.key === "Escape" && !isEditFormOpen) {
        event.preventDefault();
        handleCloseExpanded();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedItem,
    isEditFormOpen,
    handleEditItem,
    handleDeleteItem,
    handleCloseExpanded,
  ]);

  // Performance: Determine whether to use virtualization
  const shouldUseVirtualization =
    enhancedItems.length > VIRTUALIZATION_THRESHOLD;

  // Show error state if any critical data failed to load
  if (hasErrors && !isAnyLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          textAlign: "center",
          p: 3,
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading Inventory
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {itemsError
            ? "Failed to load items"
            : categoriesError
              ? "Failed to load categories"
              : locationsError
                ? "Failed to load locations"
                : merchantsError
                  ? "Failed to load merchants"
                  : "Unknown error occurred"}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading inventory..." />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        px: { xs: 2, md: 0 }, // Add horizontal padding on mobile for grid spacing
      }}
    >
      {/* Expanded Item View with lazy loading */}
      {selectedItem && (
        <>
          <Suspense
            fallback={
              <Skeleton
                variant="rectangular"
                sx={{
                  position: "fixed",
                  top: 80,
                  left: 96,
                  right: 16,
                  bottom: 16,
                  zIndex: 1000,
                }}
              />
            }
          >
            <ExpandedItemView
              selectedItem={selectedItem}
              onClose={handleCloseExpanded}
              onEdit={handleEditItem}
              onDelete={handleDeleteItem}
            />
          </Suspense>

          {/* Editing indicator overlay */}
          {isEditFormOpen && itemToEdit?.id === selectedItem.id && (
            <Box
              sx={(theme) => ({
                position: "fixed",
                zIndex: 1001,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(4px)",
                borderRadius: { xs: 0, sm: 2 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid",
                borderColor: "primary.main",
                // Responsive positioning and sizing (matches ExpandedItemView)
                [theme.breakpoints.down("md")]: {
                  // Full screen on mobile and small tablets
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 0,
                },
                // Small tablets (900-1200px): Compact overlay for problematic range
                [theme.breakpoints.between("md", "lg")]: {
                  top: 80,
                  bottom: 16,
                  left: 96,
                  width: "calc(50vw - 48px)",
                  minWidth: 400,
                  maxWidth: 500,
                },
                // Large screens (1200px+): Keep the original PERFECT sizing
                [theme.breakpoints.up("lg")]: {
                  top: 80,
                  bottom: 16,
                  left: 96,
                  width: "calc(50vw - 48px)",
                  minWidth: 600, // Original perfect minWidth
                  maxWidth: 900, // Original perfect maxWidth
                },
              })}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ mb: 1, color: "primary.main" }}>
                  Editing Item
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  "{selectedItem.name}" is being edited in a separate dialog
                </Typography>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Adaptive Grid with dynamic padding */}
      <Box sx={selectedItem ? gridPadding : {}}>
        {shouldUseVirtualization ? (
          <Suspense fallback={<LoadingSpinner message="Loading grid..." />}>
            <VirtualizedMasonryGrid spacing={2} maxColumns={maxColumns}>
              {enhancedItems.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  onView={handleViewItem}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteItem}
                  isSelected={selectedItem?.id === item.id}
                />
              ))}
            </VirtualizedMasonryGrid>
          </Suspense>
        ) : (
          <MasonryGrid spacing={2} maxColumns={maxColumns}>
            {enhancedItems.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                onView={handleViewItem}
                onEdit={handleEditItem}
                onDelete={handleDeleteItem}
                isSelected={selectedItem?.id === item.id}
              />
            ))}
          </MasonryGrid>
        )}
      </Box>

      {/* Edit Form */}
      {isEditFormOpen && itemToEdit && (
        <ItemForm
          open={isEditFormOpen}
          initialData={itemToEdit}
          mode="edit"
          onClose={handleEditFormClose}
          onSubmit={handleEditFormSubmit}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteDialogOpen && itemToDelete && (
        <DeleteConfirmDialog
          open={isDeleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          itemName={itemToDelete.name}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryPage;
